/**
 * Task/prompt mutation hooks for the Tasks area (PromptModal, useRunTask,
 * useTaskCardActions). Follows src/queries/webhooks.js's template.
 *
 * Everything here is a useMutation, even the two GETs (provider channels,
 * provider list) — both are fetched on demand from several different call
 * sites (search debounce, infinite scroll, provider switch, integration
 * connect) rather than rendered off a simple key, matching webhooks.js's
 * useWebhookConfigSecret precedent ("fetched on demand ... modeled as a
 * mutation rather than a query since there's nothing to cache/re-render on").
 *
 * The task list itself (usersContext.js's fetchTasksData/tasksData) hasn't
 * been migrated yet in this session, so callers keep invoking that imperative
 * refetch after these mutations settle instead of invalidating a query here.
 */
import { useMutation } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT, DELETE } = ReqMethods;

// Mirrors axios's default param serializer: keys with null/undefined values
// are dropped entirely, everything else (including `false`/`0`/booleans) is
// kept and stringified.
const toQueryString = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    searchParams.set(key, value);
  });
  return searchParams.toString();
};

// GET /prompts/providers/channels — 10-minute timeout preserved from the
// original axios call: some provider integrations are slow to enumerate
// channels, and a shorter client timeout would abort a request still working
// server-side.
export function useProviderChannels() {
  return useMutation({
    mutationFn: ({ profileId, userId, providerId, integration, search, pageToken }) => {
      const query = toQueryString({
        profile_id: profileId,
        user_id: userId,
        channel_type: "bot",
        include_private: true,
        ...(integration ? { integration_id: integration } : {}),
        integration: providerId,
        ...(search ? { search } : {}),
        ...(pageToken ? { page_token: pageToken } : {}),
      });
      return request(GET, "/prompts/providers/channels", { query, timeout: 600000 });
    },
  });
}




export function useAgencyPromptData() {
  return useMutation({
    mutationFn: ({ agencyId }) => {
      const query = toQueryString({
        agency_id: agencyId,
        type: "agency",
      });
      return request(GET, "/prompts/prompt_data", { query });
    },
  });
}

// "Whole team" scope for the Task Library Filter: the same endpoint as
// fetchTasksData (usersContext) but without `type=mine`, which per the
// backend's own comment falls back to its unrestricted default — every
// location/agency-shared prompt, not just the requesting user's. Kept as its
// own mutation (not folded into fetchTasksData) so switching between "You"
// and "Whole team" in the Filter doesn't clobber the shared tasksData other
// consumers of usersContext rely on.
export function useTeamPromptData() {
  return useMutation({
    mutationFn: ({ userId, locationId, agencyId }) => {
      const query = toQueryString({
        userId,
        location_id: locationId,
        agency_id: agencyId,
      });
      return request(GET, "/prompts/prompt_data", { query });
    },
  });
}

export function usePromptProviders() {
  return useMutation({
    mutationFn: ({ profileId, userId, agencyId }) => {
      const query = toQueryString({
        profile_id: profileId,
        user_id: userId,
        agency_id: agencyId,
      });
      return request(GET, "/prompts/providers", { query, timeout: 10000 });
    },
  });
}

// GET /assistant/custom-task-agent -> POST /prompts/generate, kept as ONE
// mutation because the second call's payload depends on the first call's
// assistant_id (a sequential dependent chain, not two independent fetches).
export function useGenerateTaskPrompt() {
  return useMutation({
    mutationFn: async ({
      agencyId,
      locationId,
      username,
      promptQuery,
      userId,
      fallbackAssistantId,
    }) => {
      const customTaskAssistant = await request(GET, "/assistant/custom-task-agent", {
        query: toQueryString({
          agency_id: agencyId,
          assistant_type: "Custom Task",
        }),
      });

      const assistantId =
        customTaskAssistant?.data?.assistant_id || fallbackAssistantId;

      const response = await request(POST, "/prompts/generate", {
        payload: {
          locationId,
          username,
          promptQuery,
          assistant_id: assistantId,
          user_id: userId,
        },
      });

      return {
        title: response?.data?.title || "",
        content: response?.data?.content || "",
      };
    },
  });
}

// PUT /prompts/update/scheduled (edit) or POST /prompts (create).
export function useSaveTaskPrompt() {
  return useMutation({
    mutationFn: ({ isEdit, payload }) =>
      isEdit
        ? request(PUT, "/prompts/update/scheduled", { payload })
        : request(POST, "/prompts", { payload }),
  });
}

// DELETE /prompts/delete/scheduled (scheduled task) or DELETE
// /chat/promptDelete (one-off), shared by PromptModal and useTaskCardActions.
export function useDeleteTaskPrompt() {
  return useMutation({
    mutationFn: ({ id, isScheduledTask }) => {
      const endpoint = isScheduledTask
        ? "/prompts/delete/scheduled"
        : "/chat/promptDelete";
      return request(DELETE, endpoint, { query: `_id=${id}` });
    },
  });
}

// POST /prompts/messaging (Run Task now). retry explicitly disabled: the
// server-side chain this call kicks off can take up to 30 minutes, which is
// why the timeout below is also 30 minutes — a retry after a timeout would
// re-trigger a run that's still in flight and double-deliver the result to
// Slack.
export function useRunTaskMutation() {
  return useMutation({
    mutationFn: (payload) =>
      request(POST, "/prompts/messaging", {
        payload,
        timeout: 1800000,
      }),
    retry: false,
  });
}
